import { Signal, effect } from '@preact/signals';
import { assignEmailSignal } from 'components/modal/assignEmail/signals';
import { localize } from 'i18n/localize';
import { sendSaveOfferAnalytics, type SendSaveOfferAnalyticsAdditionalData } from 'lib/dot/generic/saveOffer';
import { favoriteOffer, type FavoriteOffers, getFavoriteOffers, unfavoriteOffer } from 'service/favoriteOffer/resource';
import type { OfferData } from 'service/offer/model';
import { getUserSelf } from 'service/user/resource';
import { isRegisteredViaPhone } from 'service/user/utils';
import { user, loginOpen } from './login';
import { isSSR } from 'utils/isSSR';

class FavoriteOffersSignal extends Signal<FavoriteOffers> {
	constructor() {
		super({});

		if (!isSSR()) {
			effect(() => {
				if (user.value.state === 'login') {
					this.refresh();
				} else if (user.value.state === 'logout') {
					this.value = {};
				}
			});
		}
	}

	refresh = async (headers?: HeadersInit) => {
		this.value = await getFavoriteOffers(headers);
	};
}

export const favoriteOffers = new FavoriteOffersSignal();

const $t = localize();

export const onFavoriteClick = async (
	event: MouseEvent,
	offer: OfferData,
	analyticsData?: SendSaveOfferAnalyticsAdditionalData,
) => {
	event.preventDefault();
	const offerId = offer.id;

	if (user.peek()?.state !== 'login') {
		loginOpen(
			{ sourceComponent: 'favourites_add' },
			{
				callback: () => {
					onFavoriteClick(event, offer, analyticsData);
				},
				resetOnAccountSwitch: true,
			},
		);
		return;
	} else {
		const userData = await getUserSelf().catch(() => null);
		if (userData && isRegisteredViaPhone(userData)) {
			assignEmailSignal.open({
				title: $t.assignEmail.titleFavoriteOffer,
				loginSourceComponent: 'favourites_add',
				userData,
				afterAssignUserCallback: () => onFavoriteClick(event, offer, analyticsData),
			});
			return;
		}
	}

	if (favoriteOffers.value?.[offerId]) {
		try {
			const updatedFavoriteOffers = { ...favoriteOffers.value };
			delete updatedFavoriteOffers[offerId];
			favoriteOffers.value = updatedFavoriteOffers;

			await unfavoriteOffer(offerId);
			sendSaveOfferAnalytics(offer, false, analyticsData);
			await favoriteOffers.refresh();
		} catch (error) {
			const updatedFavoriteOffers = { ...favoriteOffers.value };
			updatedFavoriteOffers[offerId] = true;
			favoriteOffers.value = updatedFavoriteOffers;

			console.error(`Failed to unfavorite offer ${offerId}`, error);
		}
	} else {
		try {
			const updatedFavoriteOffers = { ...favoriteOffers.value };
			updatedFavoriteOffers[offerId] = true;
			favoriteOffers.value = updatedFavoriteOffers;

			await favoriteOffer(offerId);
			sendSaveOfferAnalytics(offer, true, analyticsData);
			await favoriteOffers.refresh();
		} catch (error) {
			const updatedFavoriteOffers = { ...favoriteOffers.value };
			delete updatedFavoriteOffers[offerId];
			favoriteOffers.value = updatedFavoriteOffers;

			console.error(`Failed to favorite offer ${offerId}`, error);
		}
	}
};
